/** An ordered id list as a field value (an activity's nearby activities): what each item's
 *  state is against the stored list, and the change in words for the ledger — one line
 *  per item it touches (Changed-Items-Only), each with its own revert. The list edits are
 *  the collections' (`rows.ts`). */
import type { RecordLedgerLine } from '../../editor/types';
import { idListRows, placedAsStored, withoutId } from '../../collections/rows';

export type OrderedIdState = 'kept' | 'added' | 'moved' | 'removed';

export interface OrderedIdItem {
  id: number;
  state: OrderedIdState;
  /** 1-based place in the list; null for a removed item. */
  position: number | null;
  /** 1-based place in the stored list; null for an added item. */
  storedPosition: number | null;
}

/** The kept ids whose order changed — the fewest that explain it: every kept id outside
 *  one longest run that is still in stored order. Moving one item past three others moves
 *  that item, not the three; of two that swapped places, the one that went up moved (ties
 *  keep the later run). */
export function movedIds(stored: readonly number[], list: readonly number[]): Set<number> {
  const kept = list.filter((id) => stored.includes(id));
  const at = kept.map((id) => stored.indexOf(id));
  // Longest increasing run of stored positions (O(n²): lists are short).
  const len = at.map(() => 1);
  const prev = at.map(() => -1);
  let best = -1;
  for (let i = 0; i < at.length; i += 1) {
    for (let j = 0; j < i; j += 1) {
      if (at[j]! < at[i]! && len[j]! + 1 >= len[i]!) {
        len[i] = len[j]! + 1;
        prev[i] = j;
      }
    }
    if (best < 0 || len[i]! >= len[best]!) best = i;
  }
  const inOrder = new Set<number>();
  for (let i = best; i >= 0; i = prev[i]!) inOrder.add(kept[i]!);
  return new Set(kept.filter((id) => !inOrder.has(id)));
}

/** The items a list shows, each with its state: the list (in edit mode, the draft's),
 *  then — in edit mode — the stored ids it dropped. */
export function orderedIdItems(stored: readonly number[], list: readonly number[], editing: boolean): OrderedIdItem[] {
  const moved = editing ? movedIds(stored, list) : new Set<number>();
  let position = 0;
  return idListRows(stored, editing ? list : stored, editing).map(({ id, removed }) => {
    const at = stored.indexOf(id);
    const storedPosition = at < 0 ? null : at + 1;
    if (removed) return { id, state: 'removed', position: null, storedPosition };
    position += 1;
    const state: OrderedIdState = at < 0 ? 'added' : moved.has(id) ? 'moved' : 'kept';
    return { id, state, position, storedPosition };
  });
}

/** The change in words: "Added · X", "Moved · X · from 3 to 1" in list order, then
 *  "Removed · X" in stored order. Each line takes back only itself: an added item leaves,
 *  a removed or moved one goes back where the stored order puts it. */
export function orderedIdLedgerLines(
  from: readonly number[],
  to: readonly number[],
  nameOf: (id: number) => string,
): RecordLedgerLine[] {
  const asList = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []);
  const lines: RecordLedgerLine[] = [];
  const removed: RecordLedgerLine[] = [];
  for (const item of orderedIdItems(from, to, true)) {
    const name = nameOf(item.id);
    if (item.state === 'added') {
      lines.push({ id: `added:${item.id}`, text: `Added · ${name}`, revert: (v) => withoutId(asList(v), item.id) });
    } else if (item.state === 'moved') {
      lines.push({
        id: `moved:${item.id}`,
        text: `Moved · ${name} · from ${item.storedPosition} to ${item.position}`,
        revert: (v) => placedAsStored(asList(v), from, item.id),
      });
    } else if (item.state === 'removed') {
      removed.push({ id: `removed:${item.id}`, text: `Removed · ${name}`, revert: (v) => placedAsStored(asList(v), from, item.id) });
    }
  }
  return [...lines, ...removed];
}
